import { type SEOHandle } from '@nasa-gcn/remix-seo'
import {
	appName,
	instagramUrl,
	practitionerName,
	siteOrigin,
	studioCity,
	studioCountry,
	studioLat,
	studioLng,
	studioLocality,
	studioMapsUrl,
	studioName,
	studioPostalCode,
	studioRegion,
	studioStreetAddress,
	whatsappE164,
} from './constants.ts'

export const excludeFromSitemap: SEOHandle = {
	getSitemapEntries: () => null,
}

export const robotsNoIndex = {
	name: 'robots',
	content: 'noindex, nofollow',
} as const

export function marketingMeta({
	title,
	description,
	path,
	image = '/img/patricia-session-poster.jpg',
}: {
	title: string
	description: string
	path: string
	image?: string
}) {
	const url = `${siteOrigin}${path}`
	const imageUrl = image.startsWith('http') ? image : `${siteOrigin}${image}`
	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: url },
		{ property: 'og:locale', content: 'pt_PT' },
		{ property: 'og:image', content: imageUrl },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:image', content: imageUrl },
		{ tagName: 'link', rel: 'canonical', href: url },
		{ tagName: 'link', rel: 'alternate', hrefLang: 'pt-PT', href: url },
	]
}

export function massageTherapistJsonLd() {
	return {
		'@context': 'https://schema.org',
		'@type': 'MassageTherapist',
		'@id': `${siteOrigin}/#business`,
		name: `${appName} — ${practitionerName}`,
		url: siteOrigin,
		telephone: whatsappE164,
		image: `${siteOrigin}/img/patricia-portrait.jpg`,
		priceRange: '€€',
		address: {
			'@type': 'PostalAddress',
			streetAddress: `${studioStreetAddress}, ${studioLocality}`,
			addressLocality: studioCity,
			addressRegion: studioRegion,
			postalCode: studioPostalCode,
			addressCountry: studioCountry,
		},
		geo: {
			'@type': 'GeoCoordinates',
			latitude: studioLat,
			longitude: studioLng,
		},
		hasMap: studioMapsUrl,
		containedInPlace: {
			'@type': 'LocalBusiness',
			name: studioName,
			url: studioMapsUrl,
		},
		areaServed: [
			{ '@type': 'City', name: studioCity },
			{ '@type': 'City', name: 'Coimbra' },
		],
		openingHoursSpecification: {
			'@type': 'OpeningHoursSpecification',
			dayOfWeek: [
				'Monday',
				'Tuesday',
				'Wednesday',
				'Thursday',
				'Friday',
				'Saturday',
			],
		},
		sameAs: [instagramUrl],
	}
}

export function faqPageJsonLd(
	faqs: Array<{ question: string; answer: string }>,
) {
	return {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: faqs.map((faq) => ({
			'@type': 'Question',
			name: faq.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: faq.answer,
			},
		})),
	}
}
